"use client";

import { useHelp } from "@/contexts/help-context";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BookOpen, MessageCircle } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

interface HelpBubbleProps {
  /** Short title shown in bold at the top of the tooltip. */
  title: string;
  /** One or two sentence description of what this element does. */
  description: string;
  /** Anchor on the /help page, e.g. "home-feed" → links to /help#home-feed */
  learnMoreAnchor?: string;
  /** Pre-filled question sent to the AI Advisor when "Ask BB AI" is clicked. */
  aiQuestion?: string;
  children: React.ReactNode;
  /** Which side the tooltip appears on. Default: "top" */
  side?: "top" | "bottom" | "left" | "right";
  /**
   * When true, wraps children in a block <div> instead of an inline <span>.
   * Use for section headers, cards, and other block-level content.
   */
  block?: boolean;
  /** Extra classNames on the wrapper element. */
  className?: string;
}

/**
 * HelpBubble — wraps any element with an informational tooltip.
 * Only renders the tooltip when help mode is on; when off this component is a
 * transparent passthrough. No per-element visual indicator — the global Help
 * toggle in the header is the single source of truth that help mode is active.
 * Hover any element to read its tooltip.
 */
export function HelpBubble({
  title,
  description,
  learnMoreAnchor,
  aiQuestion,
  children,
  side = "top",
  block = false,
  className,
}: HelpBubbleProps) {
  const { helpEnabled, openChatWithAI } = useHelp();

  // Passthrough — no wrapper overhead when help is disabled
  if (!helpEnabled) return <>{children}</>;

  const Wrapper = block ? "div" : "span";

  return (
    <TooltipProvider delayDuration={180}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Wrapper
            data-help-bubble-wrapped="true"
            className={cn(block ? "block" : "inline-flex", className)}
          >
            {children}
          </Wrapper>
        </TooltipTrigger>

        <TooltipContent
          side={side}
          sideOffset={10}
          className="bb-help-bubble max-w-[280px] p-0 overflow-hidden shadow-xl z-[150] rounded-[var(--radius,0.5rem)] border-2 border-primary bg-popover text-popover-foreground"
        >
          {/* Header */}
          <div className="px-3 pt-3 pb-2">
            <p className="font-semibold text-sm leading-tight mb-1">{title}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          </div>

          {/* Footer links */}
          {(learnMoreAnchor || aiQuestion) && (
            <div className="flex items-center gap-3 px-3 py-2 border-t bg-muted/30">
              {learnMoreAnchor && (
                <Link
                  href={`/help#${learnMoreAnchor}`}
                  className="flex items-center gap-1 text-[11px] text-primary hover:underline"
                >
                  <BookOpen className="w-3 h-3 flex-shrink-0" />
                  Learn more
                </Link>
              )}
              {aiQuestion && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openChatWithAI(aiQuestion);
                  }}
                  className="flex items-center gap-1 text-[11px] text-primary hover:underline ml-auto"
                >
                  <MessageCircle className="w-3 h-3 flex-shrink-0" />
                  Ask BB AI
                </button>
              )}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
